import { useEffect, useState, useRef } from "react";
import { Bell, X, Sparkles, AlertCircle, TrendingUp, BellOff, Clock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const toneIcon = (t) => t === "warning" ? AlertCircle : t === "positive" ? TrendingUp : Sparkles;
const toneColor = (t) => t === "warning" ? "text-[color:var(--terracotta)]" : t === "positive" ? "text-[color:var(--emerald)]" : "text-[color:var(--gold)]";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [nudges, setNudges] = useState([]);
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/nudges");
      setNudges(r.data);
    } catch (e) { console.error("nudges.load:", e?.message || e); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markRead = async (id) => {
    await api.post(`/nudges/${id}/read`);
    setNudges(n => n.map(x => x.nudge_id === id ? { ...x, read: true } : x));
  };
  const snooze = async (id, mode) => {
    await api.post(`/nudges/${id}/snooze`, { mode });
    setNudges(n => n.map(x => x.nudge_id === id ? { ...x, read: true, snoozed: true } : x));
    toast.success(mode === "mute_week" ? "Muted for 7 days" : "Dismissed");
  };
  const unread = nudges.filter(n => !n.read).length;

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen(!open)}
        className="w-10 h-10 rounded-full bg-[color:var(--bg-card)] border border-[rgba(212,175,55,0.15)] hover:border-[color:var(--gold)] flex items-center justify-center transition relative">
        <Bell className="w-4 h-4 text-[color:var(--gold)]" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[color:var(--terracotta)] text-white text-xs font-bold flex items-center justify-center">{unread}</span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            data-testid="nudges-drawer"
            className="absolute right-0 mt-2 w-96 glass-card shadow-2xl z-50 max-h-[70vh] overflow-auto scroll-fade">
            <div className="p-4 border-b border-[rgba(212,175,55,0.1)] flex items-center justify-between sticky top-0 bg-[color:var(--bg-card)]">
              <div className="font-heading font-semibold">Daily Coach Nudges</div>
              <button onClick={() => setOpen(false)} className="text-[color:var(--text-muted)] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="divide-y divide-[rgba(212,175,55,0.08)]">
              {nudges.length === 0 && <div className="p-6 text-center text-sm text-[color:var(--text-muted)]">No nudges yet. Come back tomorrow.</div>}
              {nudges.map((n) => {
                const Icon = toneIcon(n.tone);
                return (
                  <div key={n.nudge_id} data-testid={`nudge-${n.nudge_id}`}
                    className={`p-4 hover:bg-[rgba(212,175,55,0.04)] transition ${!n.read ? "bg-[rgba(212,175,55,0.03)]" : ""}`}>
                    <div className="flex gap-3 mb-2">
                      <Icon className={`w-4 h-4 mt-1 shrink-0 ${toneColor(n.tone)}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-sm">{n.title}</div>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-[color:var(--gold)]" />}
                        </div>
                        <div className="text-xs text-[color:var(--text-secondary)] mt-1 leading-relaxed">{n.body}</div>
                        <div className="text-[10px] text-[color:var(--text-muted)] mt-1">{n.date}</div>
                      </div>
                    </div>
                    {!n.snoozed && (
                      <div className="flex gap-2 pl-7 flex-wrap">
                        {n.cta_href && n.cta_label && (
                          <Link to={n.cta_href} onClick={() => { markRead(n.nudge_id); setOpen(false); }}
                            data-testid={`nudge-cta-${n.nudge_id}`}
                            className="text-xs px-3 py-1 rounded-full bg-[rgba(212,175,55,0.15)] border border-[color:var(--gold)] text-[color:var(--gold)] inline-flex items-center gap-1 hover:bg-[rgba(212,175,55,0.25)] transition">
                            {n.cta_label} <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                        {!n.read && (
                          <button data-testid={`nudge-read-${n.nudge_id}`} onClick={() => markRead(n.nudge_id)}
                            className="text-xs px-2.5 py-1 rounded-full border border-[rgba(212,175,55,0.2)] hover:border-[color:var(--gold)] text-[color:var(--text-secondary)] transition">
                            Mark read
                          </button>
                        )}
                        <button data-testid={`nudge-dismiss-${n.nudge_id}`} onClick={() => snooze(n.nudge_id, "dismiss")}
                          className="text-xs px-2.5 py-1 rounded-full border border-[rgba(212,175,55,0.2)] hover:border-[color:var(--terracotta)] text-[color:var(--text-secondary)] inline-flex items-center gap-1 transition">
                          <Clock className="w-3 h-3" /> Not now
                        </button>
                        <button data-testid={`nudge-mute-${n.nudge_id}`} onClick={() => snooze(n.nudge_id, "mute_week")}
                          className="text-xs px-2.5 py-1 rounded-full border border-[rgba(212,175,55,0.2)] hover:border-[color:var(--terracotta)] text-[color:var(--text-secondary)] inline-flex items-center gap-1 transition">
                          <BellOff className="w-3 h-3" /> Mute 7 days
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

